import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  const categoryTitles: Record<string, string> = {
    'short-video': t('categoryShortVideo'),
    'brand-marketing': t('categoryBrandMarketing'),
    'ad-planning': t('categoryAdPlanning'),
    'social-media': t('categorySocialMedia'),
    'content-creative': t('categoryContentCreative'),
    'full-service': t('categoryFullService'),
  };

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" description="该文章不存在或已被删除" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">该文章不存在或已被删除</p>
            <Link to="/" className="text-primary-600 font-medium hover:text-primary-700">
              {t('breadcrumbHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = category ? (categoryTitles[category.slug] || category.name) : '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://www.guodongmedia.com' },
          { '@type': 'ListItem', position: 2, name: catName, item: `https://www.guodongmedia.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.guodongmedia.com/article/${article.slug}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: { '@type': 'Organization', name: '果冻传媒' },
        url: `https://www.guodongmedia.com/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - ${t('siteName')}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},果冻传媒,${catName}`}
        canonicalUrl={`https://www.guodongmedia.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: catName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs font-medium px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap hover:bg-accent-200 transition-colors"
                  >
                    {catName}
                  </Link>
                  <span className="text-sm text-foreground-500">{article.date}</span>
                  <span className="text-sm text-foreground-500">{article.readTime}</span>
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 pb-8 border-b border-background-200/70">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary-600">{article.author[0]}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                    <div className="text-xs text-foreground-500">果冻传媒</div>
                  </div>
                </div>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full object-cover max-h-[400px]"
                />
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-li:text-foreground-700 prose-li:text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                  {t('relatedArticles')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <span className="text-xs text-foreground-500 mt-2 block">{ra.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}